import React, { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useIpeAuth } from '@/context/IpeAuthContext';
import { IPE_LOGIN_PATH } from '@/config/ipeRoutes';
import { API_BASE_URL, getIpeToken } from '@/utils/api';
import { showToast } from '@/utils/toast';
import { IpeBrandMark } from '@/components/ipe/IpeBrandLogo';
import '@/styles/ipe/ipe-auth.css';

const SCOPE_LABELS = {
  openid: '读取账号标识',
  profile: '读取展示名等基本资料',
  'shop:read': '读取店铺列表（个人/团队）',
  'shop:credential:read': '读取店铺平台凭据（用于订单系统）',
  'shop:event': '接收店铺变更事件',
};

function parseScopes(raw) {
  return String(raw || '')
    .split(/\s+/)
    .map((s) => s.trim())
    .filter(Boolean);
}

export default function PodOAuthAuthorizePage() {
  const location = useLocation();
  const navigate = useNavigate();
  const { isAuthenticated, bootstrapping } = useIpeAuth();
  const [submitting, setSubmitting] = useState(false);

  const params = useMemo(() => new URLSearchParams(location.search), [location.search]);
  const clientId = params.get('client_id') || '';
  const redirectUri = params.get('redirect_uri') || '';
  const scope = params.get('scope') || '';
  const state = params.get('state') || '';
  const scopes = parseScopes(scope);

  const returnPath = `${location.pathname}${location.search}`;

  useEffect(() => {
    if (!bootstrapping && !isAuthenticated) {
      const loginUrl = `${IPE_LOGIN_PATH}?redirect=${encodeURIComponent(returnPath)}`;
      navigate(loginUrl, { replace: true });
    }
  }, [bootstrapping, isAuthenticated, navigate, returnPath]);

  const submit = async (approved) => {
    if (submitting) return;
    setSubmitting(true);
    try {
      const token = getIpeToken();
      if (!token) {
        showToast('请先登录', 'error');
        return;
      }
      const res = await fetch(`${API_BASE_URL.replace(/\/$/, '')}/oauth/pod/consent`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          client_id: clientId,
          redirect_uri: redirectUri,
          scope,
          state,
          approved,
        }),
      });
      const payload = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error(payload?.message || `授权失败 (${res.status})`);
      }
      const url = payload?.redirectUrl || payload?.data?.redirectUrl;
      if (!url) {
        showToast('授权响应缺少跳转地址', 'error');
        return;
      }
      window.location.assign(url);
    } catch (err) {
      showToast(err?.message || '授权失败', 'error');
    } finally {
      setSubmitting(false);
    }
  };

  if (bootstrapping) {
    return (
      <div className="ipe-login-page">
        <div className="ipe-login-page__panel" style={{ maxWidth: 480, margin: '80px auto' }}>
          <p>正在检查登录状态…</p>
        </div>
      </div>
    );
  }

  return (
    <div className="ipe-login-page">
      <div className="ipe-login-page__panel" style={{ maxWidth: 520, margin: '64px auto', padding: 28 }}>
        <IpeBrandMark className="ipe-login-page__brand" textClassName="ipe-login-page__brand-text" text="IPE" />
        <h1 style={{ fontSize: 20, margin: '16px 0 8px' }}>授权 POD 访问</h1>
        <p style={{ color: '#64748b', margin: '0 0 16px', lineHeight: 1.6 }}>
          应用 <strong>POD</strong> 请求访问你的 IPE 账号与店铺数据。同意后，POD 可按个人/团队拉取店铺，
          并获取加密后的平台凭据用于订单接口。
        </p>
        <div style={{ border: '1px solid #e5e7eb', borderRadius: 8, padding: 14, marginBottom: 20 }}>
          <div style={{ fontWeight: 600, marginBottom: 8 }}>将获得的权限</div>
          <ul style={{ margin: 0, paddingLeft: 18 }}>
            {scopes.length === 0 ? (
              <li>未指定 scope</li>
            ) : (
              scopes.map((s) => (
                <li key={s} style={{ marginBottom: 4 }}>
                  {SCOPE_LABELS[s] || s}
                </li>
              ))
            )}
          </ul>
        </div>
        <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end' }}>
          <button
            type="button"
            disabled={submitting}
            onClick={() => submit(false)}
            style={{ padding: '8px 16px', borderRadius: 6, border: '1px solid #d1d5db', background: '#fff', cursor: 'pointer' }}
          >
            拒绝
          </button>
          <button
            type="button"
            disabled={submitting || !clientId || !redirectUri || !state}
            onClick={() => submit(true)}
            style={{
              padding: '8px 16px',
              borderRadius: 6,
              border: 0,
              background: '#ea580c',
              color: '#fff',
              cursor: 'pointer',
              fontWeight: 600,
            }}
          >
            {submitting ? '处理中…' : '同意授权'}
          </button>
        </div>
      </div>
    </div>
  );
}
